import React, { useState, useEffect, useMemo } from 'react';
import { bool } from 'prop-types';
import { useParams } from "react-router-dom";

import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useForm, useNav, useFormErrors } from '../../components/hooks';
import { Accordion, Loading, TableCustom, MessageModal, SectionHeading, InputCuenta, InputPersona, InputCodigo, InputEjercicio, InputEntidad, DatePickerCustom, FormLabel, FormError } from '../../components/common';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import ConceptoPagoModal from './components/ConceptoPagoModal';
import { useReporte } from '../../components/hooks/useReporte';
import { useEntidad } from '../../components/hooks/useEntidad';
import { useConfiguracion } from '../../components/hooks/useConfiguracion';
import { getDateNow, getFormatNumber } from '../../utils/convert';
import { OpenObjectURL } from '../../utils/helpers';
import { isNull } from '../../utils/validator';
import {usePermissions} from "../../components/hooks/usePermissions";
import {PERMISSIONS} from "../../consts/permissions";
import {RECIBO_TYPE} from "../../consts/reciboType";
import {TIPS} from "../../consts/tips";


function PagoReciboEspecialView(props) {

    //parametros url
    const params = useParams();

    const esDerechoEspecial = !props.reciboEspecial;

    const entityInit = {
        reciboEspecial: {
            id: 0,
            codigo: '',
            descripcion: '',
            aplicaValorUF: false,
            idTipoTributo: 0,
            activo: false,
            recibosEspecialConcepto: []
        }
    };

    //hooks
    const [state, setState] = useState({
        idCuenta: params.idCuenta ? parseInt(params.idCuenta) : 0,
        idTipoTributo: params.idCuenta ? 0 : 15,
        loading: false,
        showMessage: false
    });
    const [reciboEspecial, setReciboEspecial] = useState(entityInit.reciboEspecial);
    const [valorUF, setValorUF] = useState(0);
    const [conceptosPago, setConceptosPago] = useState([]);
    const [editConceptoModal, setEditConceptoModal] = useState({ show: false, concepto: null, conceptoPago: null });

    const [ valorDiasVencimientoVepEspecial ] = useConfiguracion({
        key: 'CuentaCorrienteDiasVencimientoVepEspecial',
        global: true,
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        }
    });
    const diasVencimientoVepEspecial = useMemo(() => {
        const dias = parseInt(valorDiasVencimientoVepEspecial);
        return isNaN(dias) ? 0 : dias;
    }, [valorDiasVencimientoVepEspecial]);

    //el valor de la UF es el de la variable que nombra la configuracion, el mismo con el que se genera el comprobante
    useConfiguracion({
        key: 'CuentaCorrienteValorUF',
        global: true,
        onLoaded: (data, isSuccess, error) => {
            if (isSuccess) {
                setValorUF(parseFloat(data.valor));
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        }
    });

    useEffect(() => {
        setConceptosPago(reciboEspecial.recibosEspecialConcepto.map(concepto => ({
            idReciboEspecialConcepto: concepto.id,
            cantidad: concepto.admiteCantidad ? concepto.cantidadDesde : 0,
            importe: concepto.importeEditable ? concepto.valor : 0
        })));
    }, [reciboEspecial]);

    const fechaVencimientoMaxima = useMemo(() => {
        let fechaVencimiento = getDateNow(false);
        fechaVencimiento.setDate(fechaVencimiento.getDate() + diasVencimientoVepEspecial);
        reciboEspecial.recibosEspecialConcepto.filter(f => !isNull(f.fechaHasta)).forEach(concepto => {
            if (concepto.fechaHasta < fechaVencimiento) fechaVencimiento = concepto.fechaHasta;
        });
        return fechaVencimiento;
    }, [diasVencimientoVepEspecial, reciboEspecial]);

    useEffect(() => {
        formSet(prev => ({...prev, fechaVencimiento: fechaVencimientoMaxima}));
    }, [fechaVencimientoMaxima]);


    const [ generateReporte ] = useReporte({
        callback: (reporte, buffer, message, filename) => {
            if (buffer)
                OpenObjectURL(filename ?? `${reporte}.pdf`, buffer);
            else
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        }
    });

    const [formValues, formHandle, , formSet] = useForm({
        idPersona: 0,
        idReciboEspecial: 0,
        fechaVencimiento: null,
        observacion: '',
    });
   
    const [persona, setPersona] = useState({
        idTipoPersona: 0,
        idTipoDocumento: 0,
        numeroDocumento: "",
        nombrePersona: ""
    });

    const [messageConfirm, setMessageConfirm] = useState({
        show: false,
        title: "",
        data: null,
        callback: null
    });

    const [, getRowEntidad] = useEntidad({
        entidades: ['Tasa','SubTasa'],
        onLoaded: (entidades, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        }
    });

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.RECIBO_ESPECIAL_EDIT);

    //definiciones
    const getDescTasa = (id) => {
        const row = getRowEntidad('Tasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }
    const getDescSubTasa = (id) => {
        const row = getRowEntidad('SubTasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }

    const conceptosTabla = useMemo(() => reciboEspecial.recibosEspecialConcepto.map(concepto => {
        const conceptoPago = conceptosPago.find(x => x.idReciboEspecialConcepto === concepto.id) ?? { cantidad: 0, importe: 0 };
        const cantidad = concepto.admiteCantidad ? conceptoPago.cantidad : 1;
        const importeUnitario = concepto.importeEditable ? conceptoPago.importe : (reciboEspecial.aplicaValorUF ? concepto.multiplicadorUF * valorUF : concepto.valor);
        return {
            ...concepto,
            cantidad,
            importeUnitario,
            subtotal: Math.round(importeUnitario * cantidad * 100) / 100
        };
    }), [reciboEspecial, conceptosPago, valorUF]);

    const GetFooterTotal = (info, columnName) => {
        const total = useMemo(
            () =>
            info.rows.reduce((sum, row) => row.values[columnName] + sum, 0),
            [info.rows]
        )

        return <>{getFormatNumber(total, 2)}</>
    }

    const tableColumns = [
        { Header: 'Tasa', Cell: (props) => getDescTasa(props.value), accessor: 'idTasa', width: '25%', disableSortBy: true },
        { Header: 'Sub tasa', Cell: (props) => getDescSubTasa(props.value), accessor: 'idSubTasa', width: '25%', disableSortBy: true },
        { Header: 'Cantidad', Cell: (props) => getFormatNumber(props.value, 0), accessor: 'cantidad', width: '10%', alignCell: 'right', disableSortBy: true },
        { Header: 'Importe unitario', Cell: (props) => getFormatNumber(props.value, 2), accessor: 'importeUnitario', width: '15%', alignCell: 'right', disableSortBy: true },
        { Header: 'Subtotal', Footer: (info) => GetFooterTotal(info, 'subtotal'), Cell: (props) => getFormatNumber(props.value, 2), id: 'subtotal', accessor: 'subtotal', width: '15%', alignCell: 'right', disableSortBy: true }
    ];
    const tableColumnsSinUF = [
        ...tableColumns
    ];
    const tableColumnsConUF = [
        tableColumns[0], tableColumns[1],
        { Header: 'Unidades fijas', Cell: (props) => getFormatNumber(props.value, 3), accessor: 'multiplicadorUF', width: '10%', alignCell: 'right', disableSortBy: true },
        ...tableColumns.slice(2)
    ];

    const avmrConceptos = {
        onModify: (row) => {
            const conceptoPago = conceptosPago.find(x => x.idReciboEspecialConcepto === row.id) ?? { idReciboEspecialConcepto: row.id, cantidad: 0, importe: 0 };
            setEditConceptoModal({ show: true, concepto: row, conceptoPago: {...conceptoPago} });
        },
        canEditRow: (row) => row.admiteCantidad || row.importeEditable
    };

    //funciones
    const getErrors = () => {

        const errors = {};

        if (formValues.idPersona === 0) errors.idPersona = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (formValues.idReciboEspecial === 0) errors.idReciboEspecial = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (isNull(formValues.fechaVencimiento)) errors.fechaVencimiento = VALIDATION_MESSAGE.REQUIRED_DATE;

        return errors;
    }

    const { errorOf, validate } = useFormErrors(getErrors);

    //handles
    const handleClickAddPagoRecibo = (idTipoRecibo) => {
        const today = getDateNow(false);

        if (!validate()) return;

        const importeTotal = conceptosTabla.reduce((acc, concepto) => acc + concepto.subtotal, 0); // = precisionRound(x,2) del backend (recibo-especial-service.ts:265-266)
        if (importeTotal <= 0) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Debe informar al menos un concepto con importe mayor a cero');
            return;
        }

        let data = {
            idCuenta: state.idCuenta,
            idPersona: formValues.idPersona,
            idTipoPersona: persona.idTipoPersona,
            idTipoDocumento: persona.idTipoDocumento,
            numeroDocumento: persona.numeroDocumento,
            nombrePersona: persona.nombrePersona,
            idReciboEspecial: formValues.idReciboEspecial,
            conceptos: conceptosPago,
            periodo: today.getFullYear().toString(),
            cuota: 0,
            fechaVencimiento: formValues.fechaVencimiento,
            derechoEspecial: esDerechoEspecial,
            idTipoRecibo: idTipoRecibo,
            observacion: formValues.observacion
        };

        const esOnline = idTipoRecibo === RECIBO_TYPE.RECIBO_WEB;
        setMessageConfirm({
            show: true,
            title: (
                <>
                    Vas a generar un {esDerechoEspecial ? 'derecho especial' : 'VEP especial'} para pago {esOnline ? 'online' : 'presencial'}.<br />
                    ¿Estás seguro de generarlo?
                    <span className="confirm-amount">
                        <span className="confirm-amount-label">Total estimado</span>
                        <span className="confirm-amount-value">$ {getFormatNumber(importeTotal, 2)}</span>
                    </span>
                </>
            ),
            data: data,
            callback: (data) => (data.derechoEspecial) ? AddPagoRecibo(data, 'derecho-especial') : AddPagoRecibo(data, 'recibo-especial')
        });
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }
    const callbackSuccessAddPagoRecibo = (response) => {
        response.json()
        .then((data) => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, `Pago ${esDerechoEspecial ? 'derecho especial' : 'VEP'} generado correctamente`, () => {
                setReciboEspecial(entityInit.reciboEspecial);
                formSet({...formValues, idReciboEspecial: 0});
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
            PrintRecibo(data.idCuentaPago);
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }
    const handleClickSalir = () => {
        if (window.opener) {
            const btnRefreshRecibos = window.opener.document.getElementById("btnRefreshRecibos");
            if (btnRefreshRecibos) btnRefreshRecibos.click();
        }
        window.close();
    }

    //funciones
    function PrintRecibo(idCuentaPago) {
        const paramsReporte = {
            idCuentaPago: idCuentaPago
        }
        generateReporte("CuentaCorrientePagoReciboEspecial", paramsReporte);
    }
    function FindContribuyente(idContribuyente) {

        const callbackSuccess = (response) => {
            response.json()
            .then((contribuyente) => {
                formSet({...formValues,
                    idPersona: contribuyente.idPersona
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            });

            setState(prevState => {
                return {...prevState, loading: false};
            });
        };
        
        setState(prevState => {
        return {...prevState, loading: true};
        });

        const paramsUrl = `/${idContribuyente}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CONTRIBUYENTE,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }
    function AddPagoRecibo(data, tipo) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const dataBody = data;
        const paramsUrl = `/pago-${tipo}`;

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.CUENTA_CORRIENTE_ITEM,
            paramsUrl,
            dataBody,
            callbackSuccessAddPagoRecibo,
            callbackNoSuccess,
            callbackError
        );

    }
    return (
    <>

        <Loading visible={state.loading}></Loading>

        {messageConfirm.show && 
        <MessageModal
            title={"Confirmación"}
            message={messageConfirm.title}
            onDismiss={() => {
                setMessageConfirm({ show: false, title: "", data: null, callback: null });
            }}
            onConfirm={() => {
                if (messageConfirm.callback) {
                    messageConfirm.callback(messageConfirm.data);
                }
                setMessageConfirm({ show: false, title: "", data: null, callback: null });
            }}
        />
        }

        {editConceptoModal.show &&
        <ConceptoPagoModal
            concepto={editConceptoModal.concepto}
            conceptoPago={editConceptoModal.conceptoPago}
            onConfirm={(nuevoConceptoPago) => {
                setConceptosPago(prev => prev.map(x => x.idReciboEspecialConcepto === nuevoConceptoPago.idReciboEspecialConcepto ? nuevoConceptoPago : x));
                setEditConceptoModal({ show: false, concepto: null, conceptoPago: null });
            }}
            onDismiss={() => setEditConceptoModal({ show: false, concepto: null, conceptoPago: null })}
        />
        }

        <SectionHeading
            titles={[
                { title: 'Facturación', },
                {
                    title: esDerechoEspecial ? 'Emisión de derecho especial' : 'Emisión de VEP especial'
                }
            ]}
        />

        <div className="m-top-20">
            <section className='section-accordion'>
                <div className="m-top-20 m-bottom-20">
                    <Accordion title="Definición" startOpen>
                        <div className='row form-basic'>

                        <div className="col-12 col-md-6 col-lg-2">
                            <label htmlFor="cuenta" className="form-label">Cuenta</label>
                            <InputCuenta
                                name="cuenta"
                                placeholder=""
                                className="form-control"
                                idTipoTributo={TRIBUTO_TYPE.CUENTAS_ESPECIALES}
                                onUpdate={({target}) => {
                                    if (target.row) {
                                        const cuenta = target.row;
                                        setState(prevState => { return {...prevState, idTipoTributo: cuenta.idTipoTributo}});
                                        FindContribuyente(cuenta.idContribuyentePrincipal);
                                    }
                                }}
                                onChange={({target}) => {
                                    setState(prevState => { return {...prevState, idCuenta: target.value}});
                                }}
                                value={state.idCuenta}
                                disabled={esDerechoEspecial}
                            />
                            <FormError />
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                            <FormLabel htmlFor="idPersona" required>Contribuyente</FormLabel>
                            <InputPersona
                                name="idPersona"
                                placeholder=""
                                className={`form-control ${errorOf('idPersona') ? 'is-invalid' : ''}`}
                                onChange={(event) => {
                                    const {target} = event;
                                    const persona = (target.row) ? {
                                      idTipoPersona: target.row.idTipoPersona,
                                      idTipoDocumento: target.row.idTipoDocumento,
                                      numeroDocumento: target.row.numeroDocumento,
                                      nombrePersona: target.row.nombrePersona
                                    } : {
                                      idTipoPersona: 0,
                                      idTipoDocumento: 0,
                                      numeroDocumento: "",
                                      nombrePersona: ""
                                    };
                                    setPersona(persona);
                                    formHandle(event);
                                  }}
                                value={formValues.idPersona}
                                idTipoPersona={0}
                                disabled={state.idCuenta > 0}
                            />
                            <FormError message={errorOf('idPersona')} />
                        </div>
                        <div className="col-12 col-md-6 col-lg-6">
                            <FormLabel htmlFor="idReciboEspecial" required>
                                {esDerechoEspecial ? 'Derecho especial' : 'VEP especial'}
                            </FormLabel>
                            <InputEntidad
                                name="idReciboEspecial"
                                placeholder=""
                                tipName={TIPS.NAME.MODALS.RECIBO_ESPECIAL_MODAL}
                                className={`form-control ${errorOf('idReciboEspecial') ? 'is-invalid' : ''}`}
                                value={formValues.idReciboEspecial}
                                title={esDerechoEspecial ? 'Derecho especial' : 'VEP especial'}
                                entidad="ReciboEspecial"
                                columns={[
                                    { Header: 'Código', accessor: 'codigo', width: '25%' },
                                    { Header: 'Descripción', accessor: 'descripcion', width: '70%' }
                                ]}
                                onChange={(event) => {
                                    if (event.target.row) {
                                        let today = getDateNow(false);
                                        let reciboEspecial = event.target.row;
                                        reciboEspecial.recibosEspecialConcepto.forEach(row => {
                                            if (row.fechaDesde) row.fechaDesde = new Date(row.fechaDesde);
                                            if (row.fechaHasta) row.fechaHasta = new Date(row.fechaHasta);
                                        });
                                        reciboEspecial.recibosEspecialConcepto = reciboEspecial.recibosEspecialConcepto.filter(f =>
                                            f.fechaDesde <= today && (isNull(f.fechaHasta) || f.fechaHasta >= today)
                                        );
                                        setReciboEspecial(reciboEspecial);
                                    }
                                    else {
                                        setReciboEspecial(entityInit.reciboEspecial);
                                    }
                                    formHandle(event);
                                }}
                                filter={x => x.activo && x.derechoEspecial === esDerechoEspecial && x.idTipoTributo === state.idTipoTributo}
                                memo={false}
                            />
                            <FormError message={errorOf('idReciboEspecial')} />
                        </div>
                        <div className="col-12 col-md-6 col-lg-2">
                            <FormLabel htmlFor="fechaVencimiento" required>Vencimiento</FormLabel>
                            <DatePickerCustom
                                name="fechaVencimiento"
                                placeholder=""
                                className={`form-control ${errorOf('fechaVencimiento') ? 'is-invalid' : ''}`}
                                onChange={formHandle}
                                value={ formValues.fechaVencimiento }
                                minValue={ getDateNow(false) }
                                maxValue={ fechaVencimientoMaxima }
                            />
                            <FormError message={errorOf('fechaVencimiento')} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="observacion" className="form-label">Observación</label>
                            <textarea
                                name="observacion"
                                placeholder=""
                                className="form-control"
                                value={formValues.observacion}
                                onChange={formHandle}
                                rows="3"
                                maxLength="600"
                            />
                        </div>

                        </div>
                    </Accordion>

                    {reciboEspecial.id > 0 &&
                    <Accordion title="Conceptos" startOpen>
                        <div className='row form-basic'>
                            <div className="m-bottom-50">

                                <TableCustom
                                    showFilterGlobal={false}
                                    showFooter
                                    className={'TableCustomBase'}
                                    columns={(reciboEspecial.aplicaValorUF) ? tableColumnsConUF : tableColumnsSinUF}
                                    data={conceptosTabla}
                                    avmr={avmrConceptos}
                                    disabled={!canEdit}
                                    messageEmpty={`El ${esDerechoEspecial ? 'derecho especial' : 'VEP especial'} no tiene conceptos vigentes`}
                                />

                            </div>
                        </div>
                    </Accordion>
                    }
                </div>
            </section>
        </div>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                {reciboEspecial.recibosEspecialConcepto.length > 0 &&
                <button
                    className={`btn action-button float-end m-left-10 ${!canEdit ? 'disabled' : ''}`}
                    onClick={ (event) => handleClickAddPagoRecibo(RECIBO_TYPE.RECIBO_WEB) }>Generar VEP online</button>
                }
                {reciboEspecial.recibosEspecialConcepto.length > 0 &&
                <button
                    className={`btn action-button float-end m-left-10 ${!canEdit ? 'disabled' : ''}`}
                    onClick={ (event) => handleClickAddPagoRecibo(RECIBO_TYPE.RECIBO_MOSTRADOR) }>Generar VEP presencial</button>
                }
                {esDerechoEspecial &&
                <button className="btn back-button float-start" onClick={ (event) => handleClickSalir() }>Volver</button>
                }
            </div>
        </footer>
        
    </>
    )
}

PagoReciboEspecialView.propTypes = {
    reciboEspecial: bool.isRequired
};

export default PagoReciboEspecialView;
